import { useState, useEffect, useCallback, useRef } from "react";
import api from "../../services/api";

const CATEGORY_CONFIG = {
  saas:        { label: "SaaS / Logiciel",      icon: "🤖", color: "#7c3aed" },
  ndd:         { label: "Nom de domaine",        icon: "🌐", color: "#185fa5" },
  email:       { label: "Email",                 icon: "📧", color: "#1a9e72" },
  serveur:     { label: "Serveur / Hébergement", icon: "🖥️", color: "#d97706" },
  hébergement: { label: "Hébergement",           icon: "🖥️", color: "#d97706" },
  logiciel:    { label: "Logiciel",              icon: "💾", color: "#6b7280" },
  télécom:     { label: "Télécom",               icon: "📞", color: "#e24b4a" },
  autre:       { label: "Autre",                 icon: "📦", color: "#6b7280" },
};

const CYCLE_LABELS = { mensuel: "/mois", trimestriel: "/trim.", annuel: "/an", biennal: "/2 ans" };

function fmt(n) { return (n||0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtDate(d) { if (!d) return "—"; return new Date(d + "T00:00:00").toLocaleDateString("fr-FR"); }
function daysUntil(d) { return Math.ceil((new Date(d + "T00:00:00") - new Date()) / 86400000); }

const EMPTY_FORM = {
  name: "", category: "saas", supplier_name: "", amount: "", currency: "EUR",
  billing_cycle: "mensuel", next_renewal_date: "", auto_renew: true,
  payment_method: "", account_id: "", login_url: "", notes: "", is_active: true, alert_days_before: "30",
};

// ── OCR Import Modal ──────────────────────────────────────────────────────
// Migré vers le pipeline OCR unifié (services/supplierInvoiceOcr.js) le
// 21/07/2026 : cascade gratuite (PDF natif) avant recours à l'IA (clé
// serveur centralisée, Mistral par défaut depuis 09/2026 — voir
// aiKey.js), staging + numérotation atomique + écriture via le circuit
// comptable centralisé, au lieu de l'ancien appel direct à Claude et de
// l'écriture en dur D 606xxx/C 401 dans routes/abonnements.js. Le palier
// intermédiaire Tesseract prévu à l'origine n'a jamais fonctionné et a été
// retiré (09/2026) — voir l'en-tête de supplierInvoiceOcr.js.
// L'UX à un clic est conservée : la relecture manuelle du formulaire ci-
// dessous constitue la validation humaine ; upload + correction + passage
// en compta s'enchaînent donc automatiquement au clic "Enregistrer".
function OcrImportModal({ subscriptions, currentCompanyId, onClose, onImported }) {
  const [file, setFile]         = useState(null);
  const [scanning, setScanning] = useState(false);
  const [stagingId, setStagingId] = useState(null);
  const [form, setForm]         = useState(null);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");
  const fileRef = useRef();

  const scanPdf = async (f) => {
    setFile(f); setStagingId(null); setForm(null); setError(""); setScanning(true);
    try {
      const r = await api.ocrInvoices.upload(currentCompanyId, f);
      setStagingId(r.id);
      setForm({
        supplier_name: r.supplier_name || "",
        invoice_ref: r.invoice_ref || "",
        invoice_date: r.invoice_date || new Date().toISOString().slice(0, 10),
        amount_ht: r.amount_ht != null ? String(r.amount_ht) : "",
        amount_tva: r.amount_tva != null ? String(r.amount_tva) : "",
        amount_ttc: r.amount_ttc != null ? String(r.amount_ttc) : "",
        currency: "EUR",
        subscription_id: "",
        suggested_account: r.suggested_account || "",
        ocr_level: r.ocr_level,
        confidence: r.confidence,
      });
    } catch (err) {
      // Doublon détecté par le backend — message clair plutôt qu'une erreur technique
      if (err.message && err.message.includes('déjà été importée')) setError("Cette facture a déjà été importée précédemment.");
      else setError(err.message);
    }
    finally { setScanning(false); }
  };

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      // 1. Applique les corrections éventuelles + l'association à l'abonnement
      await api.ocrInvoices.update(stagingId, {
        supplier_name: form.supplier_name,
        invoice_ref: form.invoice_ref,
        invoice_date: form.invoice_date,
        amount_ht: form.amount_ht,
        amount_tva: form.amount_tva,
        amount_ttc: form.amount_ttc,
        suggested_account: form.suggested_account,
        subscription_id: form.subscription_id || null,
      });
      // 2. Valide -> crée purchase_invoices + écriture comptable (compte confirmé par l'utilisateur)
      await api.ocrInvoices.validate(stagingId, { compte_comptable: form.suggested_account || undefined });
      onImported(); onClose();
    } catch (err) { setError(err.message); setSaving(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "40px 16px" }}>
      <div className="card" style={{ width: "100%", maxWidth: 600 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>🤖 Import facture PDF par OCR</span>
          <button className="btn" style={{ marginLeft: "auto" }} onClick={onClose}>✕</button>
        </div>
        {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{error}</div>}

        {!form ? (
          <div
            style={{ border: "2px dashed #d1d5db", borderRadius: 10, padding: "40px 24px", textAlign: "center", cursor: "pointer", background: "#fafafa" }}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); scanPdf(e.dataTransfer.files[0]); }}
            onClick={() => !scanning && fileRef.current?.click()}
          >
            {scanning ? (
              <><div style={{ fontSize: 32, marginBottom: 8 }}>🤖</div><div style={{ fontWeight: 600 }}>Analyse en cours par l'IA…</div></>
            ) : (
              <><div style={{ fontSize: 32, marginBottom: 8 }}>📄</div><div style={{ fontWeight: 600, marginBottom: 4 }}>Glissez un PDF de facture ici</div><div style={{ fontSize: 12, color: "var(--text-muted)" }}>ou cliquez pour sélectionner</div></>
            )}
            <input ref={fileRef} type="file" accept=".pdf" style={{ display: "none" }} onChange={e => scanPdf(e.target.files[0])} />
          </div>
        ) : (
          <>
            <div style={{ padding: "8px 12px", background: "#1a9e7215", border: "1px solid #1a9e7230", borderRadius: 8, marginBottom: 16, fontSize: 13, color: "#1a9e72" }}>
              ✅ Données extraites — vérifiez et corrigez si nécessaire.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              {[["Fournisseur", "supplier_name", "text"], ["N° Facture", "invoice_ref", "text"], ["Date", "invoice_date", "date"], ["Devise", "currency", "text"]].map(([l, k, t]) => (
                <div key={k}>
                  <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 3 }}>{l}</label>
                  <input className="input" type={t} value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} />
                </div>
              ))}
              {[["Montant HT (€)", "amount_ht"], ["Montant TVA (€)", "amount_tva"], ["Montant TTC (€)", "amount_ttc"]].map(([l, k]) => (
                <div key={k}>
                  <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 3 }}>{l}</label>
                  <input className="input" type="number" step="0.01" value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} />
                </div>
              ))}
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 3 }}>Associer à l'abonnement</label>
                <select className="input" value={form.subscription_id} onChange={e => setForm(f => ({ ...f, subscription_id: e.target.value }))}>
                  <option value="">— Non associé —</option>
                  {subscriptions.map(s => <option key={s.id} value={s.id}>{s.name} ({s.supplier_name})</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn" onClick={() => setForm(null)}>← Rescanner</button>
              <button className="btn" onClick={onClose}>Annuler</button>
              <button className="btn primary" disabled={saving} onClick={handleSave}>{saving ? "Sauvegarde…" : "Enregistrer la facture"}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Subscription Form Modal ───────────────────────────────────────────────
function SubFormModal({ initial, onClose, onSaved, currentCompanyId }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");
  const isEdit = !!initial?.id;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setError("");
    try {
      const data = { ...form, company_id: currentCompanyId, amount: parseFloat(form.amount), alert_days_before: parseInt(form.alert_days_before) };
      if (isEdit) await api.abonnements.updateSupplierSub(initial.id, data);
      else        await api.abonnements.createSupplierSub(data);
      onSaved(); onClose();
    } catch (err) { setError(err.message); setSaving(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "40px 16px" }}>
      <div className="card" style={{ width: "100%", maxWidth: 640 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{isEdit ? "✏️ Modifier l'abonnement" : "➕ Nouvel abonnement fournisseur"}</span>
          <button className="btn" style={{ marginLeft: "auto" }} onClick={onClose}>✕</button>
        </div>
        {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div style={{ gridColumn: "1/-1" }}>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Nom *</label>
              <input className="input" required value={form.name} onChange={e => set("name", e.target.value)} placeholder="ex: Brevo Pro, OVH Serveur..." />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Catégorie</label>
              <select className="input" value={form.category} onChange={e => set("category", e.target.value)}>
                {Object.entries(CATEGORY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Fournisseur</label>
              <input className="input" value={form.supplier_name} onChange={e => set("supplier_name", e.target.value)} placeholder="Brevo, OVH, Stripe..." />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Montant *</label>
              <input className="input" type="number" step="0.01" required value={form.amount} onChange={e => set("amount", e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Cycle de facturation</label>
              <select className="input" value={form.billing_cycle} onChange={e => set("billing_cycle", e.target.value)}>
                <option value="mensuel">Mensuel</option>
                <option value="trimestriel">Trimestriel</option>
                <option value="annuel">Annuel</option>
                <option value="biennal">Biennal</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Prochain renouvellement *</label>
              <input className="input" type="date" required value={form.next_renewal_date} onChange={e => set("next_renewal_date", e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Méthode de paiement</label>
              <input className="input" value={form.payment_method} onChange={e => set("payment_method", e.target.value)} placeholder="CB, SEPA, PayPal..." />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Identifiant compte</label>
              <input className="input" value={form.account_id} onChange={e => set("account_id", e.target.value)} placeholder="email@example.com" />
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>URL connexion</label>
              <input className="input" type="url" value={form.login_url} onChange={e => set("login_url", e.target.value)} placeholder="https://app.brevo.com" />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Alerter J-</label>
              <input className="input" type="number" min="1" value={form.alert_days_before} onChange={e => set("alert_days_before", e.target.value)} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, paddingTop: 20 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="checkbox" checked={form.auto_renew} onChange={e => set("auto_renew", e.target.checked)} />
                <span style={{ fontSize: 12, fontWeight: 600 }}>Renouvellement automatique</span>
              </label>
            </div>
            <div style={{ gridColumn: "1/-1" }}>
              <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>Notes</label>
              <textarea className="input" rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} style={{ resize: "vertical" }} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn primary" disabled={saving}>{saving ? "Enregistrement…" : isEdit ? "Sauvegarder" : "Créer"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Invoices Panel ────────────────────────────────────────────────────────
function InvoicesPanel({ sub, onClose }) {
  const [invoices, setInvoices] = useState([]);
  useEffect(() => {
    api.abonnements.subInvoices(sub.id).then(setInvoices).catch(console.error);
  }, [sub.id]);

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="card" style={{ width: "100%", maxWidth: 580, maxHeight: "80vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
          <span style={{ fontWeight: 700 }}>📄 Factures — {sub.name}</span>
          <button className="btn" style={{ marginLeft: "auto" }} onClick={onClose}>✕</button>
        </div>
        {invoices.length === 0 ? (
          <div style={{ textAlign: "center", padding: 32, color: "var(--text-muted)", fontSize: 13 }}>Aucune facture importée</div>
        ) : invoices.map(inv => (
          <div key={inv.id} style={{ display: "flex", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f3f4f6", gap: 10 }}>
            <span style={{ fontSize: 20 }}>🧾</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{inv.invoice_ref || "Sans n°"} — {inv.supplier_name}</div>
              <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{fmtDate(inv.invoice_date)} · {inv.source === 'email_import' ? '📧' : '📤'} {inv.source}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontWeight: 700 }}>{fmt(inv.amount_ttc)} {inv.currency}</div>
              <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 8, background: inv.is_processed ? "#1a9e7215" : "#f59e0b15", color: inv.is_processed ? "#1a9e72" : "#d97706" }}>
                {inv.is_processed ? "Comptabilisé" : "À valider"}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Main SupplierSubsTab
// ════════════════════════════════════════════════════════════════════════
export default function SupplierSubsTab({ currentCompanyId }) {
  const [subs, setSubs]           = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [catFilter, setCatFilter] = useState("");
  const [showForm, setShowForm]   = useState(false);
  const [editSub, setEditSub]     = useState(null);
  const [showOcr, setShowOcr]     = useState(false);
  const [viewInv, setViewInv]     = useState(null);
  const [toast, setToast]         = useState("");
  const [emailImports, setEmailImports] = useState([]);
  const [showEmailImports, setShowEmailImports] = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    Promise.all([
      api.abonnements.supplierSubs(currentCompanyId),
      api.abonnements.supplierDashboard(currentCompanyId),
    ]).then(([r, d]) => { setSubs(r); setDashboard(d); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cet abonnement ?")) return;
    await api.abonnements.deleteSupplierSub(id).catch(console.error);
    load();
  };

  const openEmailImports = async () => {
    const r = await api.abonnements.emailImports(currentCompanyId).catch(() => []);
    setEmailImports(r);
    setShowEmailImports(true);
  };

  const filtered = catFilter ? subs.filter(s => s.category === catFilter) : subs;

  return (
    <div>
      {toast && <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 2000, background: "#1a9e72", color: "#fff", padding: "12px 20px", borderRadius: 10, fontWeight: 600, fontSize: 14 }}>{toast}</div>}

      {/* KPIs */}
      {dashboard && (
        <div className="kpi-grid k4" style={{ marginBottom: 16 }}>
          <div className="kpi"><div className="kpi-label">💶 Total /mois</div><div className="kpi-value">{fmt(dashboard.monthly)} €</div></div>
          <div className="kpi"><div className="kpi-label">📅 Total /an</div><div className="kpi-value">{fmt(dashboard.yearly)} €</div></div>
          <div className="kpi">
            <div className="kpi-label">🔄 Renouvellements J-30</div>
            <div className="kpi-value" style={{ color: dashboard.renewals30 > 0 ? "#d97706" : "inherit" }}>{dashboard.renewals30}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">⚠️ Urgents (J-7)</div>
            <div className="kpi-value" style={{ color: dashboard.urgent > 0 ? "#e24b4a" : "inherit" }}>{dashboard.urgent}</div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <button className="btn primary" onClick={() => { setEditSub(null); setShowForm(true); }}>+ Ajouter un abonnement</button>
        <button className="btn" onClick={() => setShowOcr(true)}>🤖 Importer facture PDF</button>
        <button className="btn" onClick={openEmailImports}>📧 File d'attente email ({emailImports.length})</button>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button onClick={() => setCatFilter("")} style={{ padding: "4px 10px", borderRadius: 16, fontSize: 11, fontWeight: 600, cursor: "pointer", border: "1px solid #e5e7eb", background: !catFilter ? "var(--g)" : "#fff", color: !catFilter ? "#fff" : "var(--text-muted)" }}>Tous</button>
          {Object.entries(CATEGORY_CONFIG).map(([k, v]) => (
            <button key={k} onClick={() => setCatFilter(k === catFilter ? "" : k)} style={{ padding: "4px 10px", borderRadius: 16, fontSize: 11, fontWeight: 600, cursor: "pointer", border: `1px solid ${v.color}33`, background: catFilter === k ? v.color + "22" : "#fff", color: catFilter === k ? v.color : "var(--text-muted)" }}>
              {v.icon} {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? <div style={{ textAlign: "center", padding: 48, color: "var(--text-muted)" }}>Chargement…</div> :
       filtered.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 56 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📦</div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Aucun abonnement fournisseur</div>
          <button className="btn primary" onClick={() => setShowForm(true)}>+ Ajouter le premier abonnement</button>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
            <thead>
              <tr style={{ background: "#f8fafc", fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase" }}>
                {["Abonnement", "Catégorie", "Montant", "Cycle", "Renouvellement", "Auto", "Statut", "Actions"].map(h => (
                  <th key={h} style={{ padding: "9px 14px", textAlign: h === "Montant" ? "right" : "left", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(sub => {
                const cat     = CATEGORY_CONFIG[sub.category] || CATEGORY_CONFIG.autre;
                const days    = daysUntil(sub.next_renewal_date);
                const urgent  = days <= 7;
                const warning = days <= 30;
                return (
                  <tr key={sub.id} style={{ borderTop: "1px solid #f3f4f6", fontSize: 13 }}>
                    <td style={{ padding: "10px 14px" }}>
                      <div style={{ fontWeight: 700 }}>{sub.name}</div>
                      {sub.supplier_name && <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{sub.supplier_name}</div>}
                      {sub.login_url && <a href={sub.login_url} target="_blank" rel="noreferrer" style={{ fontSize: 10, color: "var(--g)" }}>🔗 Connexion</a>}
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      <span style={{ fontSize: 12, color: cat.color }}>{cat.icon} {cat.label}</span>
                    </td>
                    <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700 }}>
                      {fmt(sub.amount)} {sub.currency}
                      <div style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 400 }}>{CYCLE_LABELS[sub.billing_cycle]}</div>
                    </td>
                    <td style={{ padding: "10px 14px", fontSize: 12 }}>{sub.billing_cycle}</td>
                    <td style={{ padding: "10px 14px" }}>
                      <div style={{ color: urgent ? "#e24b4a" : warning ? "#d97706" : "inherit", fontWeight: urgent || warning ? 700 : 400 }}>
                        {fmtDate(sub.next_renewal_date)}
                      </div>
                      {days >= 0 && <div style={{ fontSize: 10, color: urgent ? "#e24b4a" : warning ? "#d97706" : "var(--text-muted)" }}>J-{days}</div>}
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      {sub.auto_renew ? <span style={{ color: "#1a9e72", fontSize: 12 }}>✅ Oui</span> : <span style={{ color: "#6b7280", fontSize: 12 }}>Non</span>}
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 10, fontWeight: 700, background: sub.is_active ? "#1a9e7215" : "#6b728015", color: sub.is_active ? "#1a9e72" : "#6b7280" }}>
                        {sub.is_active ? "Actif" : "Résilié"}
                      </span>
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button className="btn" style={{ fontSize: 11 }} onClick={() => setViewInv(sub)}>📄</button>
                        <button className="btn" style={{ fontSize: 11 }} onClick={() => { setEditSub(sub); setShowForm(true); }}>✏️</button>
                        <button className="btn" style={{ fontSize: 11, color: "#e24b4a" }} onClick={() => handleDelete(sub.id)}>🗑</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Email imports queue */}
      {showEmailImports && (
        <div style={{ position: "fixed", inset: 0, background: "#0009", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="card" style={{ width: "100%", maxWidth: 700, maxHeight: "80vh", overflowY: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
              <span style={{ fontWeight: 700 }}>📧 File d'attente — emails importés</span>
              <button className="btn" style={{ marginLeft: "auto" }} onClick={() => setShowEmailImports(false)}>✕</button>
            </div>
            {emailImports.length === 0 ? (
              <div style={{ textAlign: "center", padding: 32, color: "var(--text-muted)", fontSize: 13 }}>Aucun email en file d'attente</div>
            ) : emailImports.map(ei => (
              <div key={ei.id} style={{ display: "flex", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f3f4f6", gap: 12 }}>
                <span style={{ fontSize: 22 }}>📧</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{ei.subject || "(sans objet)"}</div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{ei.from_email} · {new Date(ei.received_at || ei.created_at).toLocaleDateString("fr-FR")}</div>
                  <div style={{ fontSize: 11 }}>{ei.attachment_name}</div>
                </div>
                <div>
                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 8, fontWeight: 700, background: ei.status === "traité" ? "#1a9e7215" : ei.status === "erreur" ? "#e24b4a15" : "#f59e0b15", color: ei.status === "traité" ? "#1a9e72" : ei.status === "erreur" ? "#e24b4a" : "#d97706" }}>{ei.status}</span>
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  {ei.status === "reçu" && <button className="btn primary" style={{ fontSize: 11 }} onClick={() => { api.abonnements.processEmailImport(ei.id, "traité"); setEmailImports(r => r.map(e => e.id === ei.id ? { ...e, status: "traité" } : e)); }}>✅ Valider</button>}
                  <button className="btn" style={{ fontSize: 11, color: "#6b7280" }} onClick={() => { api.abonnements.processEmailImport(ei.id, "ignoré"); setEmailImports(r => r.map(e => e.id === ei.id ? { ...e, status: "ignoré" } : e)); }}>Ignorer</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showForm   && <SubFormModal initial={editSub} currentCompanyId={currentCompanyId} onClose={() => { setShowForm(false); setEditSub(null); }} onSaved={() => { load(); showToast(editSub ? "✅ Abonnement modifié" : "✅ Abonnement créé"); }} />}
      {showOcr    && <OcrImportModal subscriptions={subs} currentCompanyId={currentCompanyId} onClose={() => setShowOcr(false)} onImported={() => { load(); showToast("✅ Facture importée"); }} />}
      {viewInv    && <InvoicesPanel sub={viewInv} onClose={() => setViewInv(null)} />}
    </div>
  );
}
